import React, { useMemo } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { useNavigate } from "react-router-dom";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import {
    buildStatusLabel,
    getStatusVariant,
    hasStatus,
    buildBfdLabel,
    getBfdVariant,
    buildReflectorDownLabel,
} from "../../helpers/bgpValidation";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface OverviewReflector {
    id: string;
    name: string;
    description: string;
    subId: string;
    status: string;
    cloud: string;
    region: string;
    version: string;
}

export interface OverviewCluster {
    id: string;
    name: string;
    status: string;
    cloud: string;
    region: string;
    description: string;
}

export interface BgpSession {
    id: string;
    reflectorInstanceId: string;
    reflectorName: string;
    reflectorDescription: string;
    neighborAddress: string;
    neighborDescription: string;
    operatingMode: string;
    status: string;
    statusChangedAt: number | null;
    localAs: number;
    peerAs: number;
    prefixesReceived: number;
    prefixesAdvertised: number;
    BFD: boolean;
    bfdLocalState: string | null;
    bfdRemoteState: string | null;
    bfdStatusChangedAt: number | null;
    // Liveness of the owning reflector (websocket-connected flag). When
    // false, status/BFD/prefix values are frozen at their last reported
    // state and must not be rendered as live. Null = unknown (old backend).
    reflectorOnline: boolean | null;
    reflectorLastUpdate: string | number | null;
}

/* ------------------------------------------------------------------ */
/*  Column definitions                                                 */
/* ------------------------------------------------------------------ */

export function useReflectorColumns(): GenericTableColumn<OverviewReflector>[] {
    const navigate = useNavigate();
    return useMemo(
        () => [
            {
                header: "ID / name",
                accessor: "name" as keyof OverviewReflector,
                renderCell: (row: OverviewReflector) => (
                    <div>
                        <button
                            className="text-left hover:text-semantic-text-brand hover:underline font-medium underline text-semantic-text-brand"
                            {...viewHref(buildDetailPath("bgpReflector", { id: row.id }))}
                            onClick={() =>
                                navigate(buildDetailPath("bgpReflector", { id: row.id }), {
                                    state: { selectedReflectorRow: row },
                                })
                            }
                        >
                            {row.name}
                        </button>
                        <span className="block text-xs text-semantic-text-secondary">
                            {row.description || row.subId}
                        </span>
                    </div>
                ),
            },
            {
                header: "Status",
                accessor: "status" as keyof OverviewReflector,
                renderCell: (row: OverviewReflector) => (
                    <GenericTag
                        label={row.status}
                        variant={row.status === "Online" ? "success" : "error"}
                        size="small"
                        rounded="full"
                    />
                ),
            },
            {
                header: "Cloud",
                accessor: "cloud" as keyof OverviewReflector,
                renderCell: (row: OverviewReflector) => (
                    <GenericTag
                        label={row.cloud}
                        variant={row.cloud === "AWS" ? "warning" : row.cloud === "GCP" ? "success" : "neutral"}
                        size="small"
                        rounded="full"
                    />
                ),
            },
            {
                header: "Region",
                accessor: "region" as keyof OverviewReflector,
                renderCell: (row: OverviewReflector) => (
                    <GenericTag label={row.region} variant="neutral" size="small" rounded="full" />
                ),
            },
            {
                header: "Version",
                accessor: "version" as keyof OverviewReflector,
            },
        ],
        [navigate]
    );
}

export function useClusterColumns(): GenericTableColumn<OverviewCluster>[] {
    const navigate = useNavigate();
    return useMemo(
        () => [
            {
                header: "ID / name",
                accessor: "name" as keyof OverviewCluster,
                renderCell: (row: OverviewCluster) => (
                    <div>
                        <button
                            className="text-left hover:text-semantic-text-brand hover:underline font-medium underline text-semantic-text-brand"
                            {...viewHref(buildDetailPath("cluster", { id: row.id }))}
                            onClick={() =>
                                navigate(buildDetailPath("cluster", { id: row.id }))
                            }
                        >
                            {row.name}
                        </button>
                        <span className="block text-xs text-semantic-text-secondary">{row.description || row.id}</span>
                    </div>
                ),
            },
            {
                header: "Status",
                accessor: "status" as keyof OverviewCluster,
                renderCell: (row: OverviewCluster) => (
                    <GenericTag
                        label={row.status}
                        variant={row.status === "Online" ? "success" : row.status === "Offline" ? "error" : "warning"}
                        size="small"
                        rounded="full"
                    />
                ),
            },
            {
                header: "Cloud",
                accessor: "cloud" as keyof OverviewCluster,
                renderCell: (row: OverviewCluster) => (
                    <GenericTag
                        label={row.cloud}
                        variant={row.cloud === "AWS" ? "warning" : row.cloud === "GCP" ? "success" : "neutral"}
                        size="small"
                        rounded="full"
                    />
                ),
            },
            {
                header: "Region",
                accessor: "region" as keyof OverviewCluster,
                renderCell: (row: OverviewCluster) => (
                    <GenericTag label={row.region} variant="neutral" size="small" rounded="full" />
                ),
            },
            {
                header: "Description",
                accessor: "description" as keyof OverviewCluster,
            },
        ],
        [navigate]
    );
}

export function useBgpSessionColumns(): GenericTableColumn<BgpSession>[] {
    const navigate = useNavigate();
    return useMemo(
        () => [
            {
                header: "CTX-R / description",
                accessor: "reflectorName" as keyof BgpSession,
                renderCell: (row: BgpSession) => (
                    <div>
                        <button
                            className="text-left hover:text-semantic-text-brand hover:underline font-medium underline text-semantic-text-brand"
                            {...viewHref(row.reflectorInstanceId ? buildDetailPath("bgpReflector", { id: row.reflectorInstanceId }) : null)}
                            onClick={() =>
                                navigate(buildDetailPath("bgpReflector", { id: row.reflectorInstanceId }))
                            }
                        >
                            {row.reflectorName}
                        </button>
                        <span className="block text-xs text-semantic-text-secondary">{row.reflectorDescription}</span>
                    </div>
                ),
            },
            {
                header: "Neighbor / description",
                accessor: "neighborAddress" as keyof BgpSession,
                renderCell: (row: BgpSession) => (
                    <div>
                        <span className="block text-semantic-text-primary">{row.neighborAddress}</span>
                        <span className="block text-xs text-semantic-text-secondary">{row.neighborDescription}</span>
                    </div>
                ),
            },
            {
                header: "Mode",
                accessor: "operatingMode" as keyof BgpSession,
                renderCell: (row: BgpSession) => {
                    const mode = (row.operatingMode || "").toLowerCase();
                    const displayMode = mode === "enterprise" ? "Enterprise" : "Telco";
                    return (
                        <GenericTag
                            label={displayMode}
                            variant={mode === "enterprise" ? "neutral" : "warning"}
                        />
                    );
                },
            },
            {
                header: "Status",
                accessor: "status" as keyof BgpSession,
                renderCell: (row: BgpSession) => {
                    // Reflector not reporting: its persisted status is frozen
                    // at the last known value — say "no data" instead of
                    // presenting the stale state as live (mirrors the
                    // cluster BGP view treatment).
                    if (row.reflectorOnline === false) {
                        return (
                            <GenericTag
                                label={buildReflectorDownLabel(row.reflectorLastUpdate)}
                                variant="default"
                            />
                        );
                    }
                    if (!hasStatus(row.status)) return <GenericTag label="-" variant="default" />;
                    return (
                        <GenericTag
                            label={buildStatusLabel(row.status, row.statusChangedAt ?? undefined)}
                            variant={getStatusVariant(row.status)}
                        />
                    );
                },
            },
            {
                header: "Local AS",
                accessor: "localAs" as keyof BgpSession,
                renderCell: (row: BgpSession) => (
                    <span className="text-semantic-text-primary">{row.localAs}</span>
                ),
            },
            {
                header: "Peer AS",
                accessor: "peerAs" as keyof BgpSession,
                renderCell: (row: BgpSession) => (
                    <span className="text-semantic-text-primary">{row.peerAs}</span>
                ),
            },
            {
                header: "Rcvd / Adv",
                accessor: "prefixesReceived" as keyof BgpSession,
                renderCell: (row: BgpSession) => {
                    // Frozen counters from a down reflector are meaningless.
                    if (row.reflectorOnline === false) {
                        return <span className="text-semantic-text-secondary">-</span>;
                    }
                    return (
                        <span className="font-mono text-xs whitespace-nowrap text-semantic-text-primary">
                            {row.prefixesReceived} / {row.prefixesAdvertised}
                        </span>
                    );
                },
            },
            {
                header: "BFD Status",
                accessor: "bfdLocalState" as keyof BgpSession,
                renderCell: (row: BgpSession) => {
                    // Reflector not reporting: its persisted BFD state is
                    // frozen, hide it (mirrors the cluster BGP view).
                    if (row.reflectorOnline === false) {
                        return <span className="text-semantic-text-secondary">-</span>;
                    }
                    if (!row.BFD) {
                        return <GenericTag label="Disable" variant="default" size="small" />;
                    }
                    const label = buildBfdLabel(
                        row.BFD,
                        row.bfdLocalState ?? undefined,
                        row.bfdRemoteState ?? undefined,
                        row.bfdStatusChangedAt ?? undefined,
                    );
                    if (!label) {
                        return <span className="text-semantic-text-secondary">-</span>;
                    }
                    return (
                        <GenericTag
                            label={label}
                            variant={getBfdVariant(
                                row.BFD,
                                row.bfdLocalState ?? undefined,
                                row.bfdRemoteState ?? undefined,
                            )}
                            size="small"
                        />
                    );
                },
            },
        ],
        [navigate]
    );
}

/* ------------------------------------------------------------------ */
/*  Filtering helper                                                   */
/* ------------------------------------------------------------------ */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function filterRows<T extends Record<string, any>>(data: T[], filters: ParsedFilter[]): T[] {
    if (filters.length === 0) return data;
    return data.filter((row) =>
        filters.every((f) => {
            const val = f.value.toLowerCase();
            return Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(val));
        })
    );
}
